import { supabase } from "@/integrations/supabase/client";
import type { ArenaMediaAsset, MatchMediaScope } from "@/types/services/api-contracts";

import { requireAuthenticatedUser } from "./_core/auth";

const MATCH_MEDIA_BUCKET = "match-media" as const;
const MATCH_MEDIA_MAX_BYTES = 5 * 1024 * 1024;
const MATCH_MEDIA_EXTENSIONS: Record<string, string> = {
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
};

function buildSafeMediaFileName(file: File): string {
  const extension = MATCH_MEDIA_EXTENSIONS[file.type];
  if (!extension) throw new Error("Tipo de imagem nao permitido. Use JPG, PNG ou WebP.");
  const randomId =
    globalThis.crypto?.randomUUID?.() ??
    `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  return `${randomId}.${extension}`;
}

export const matchMediaService = {
  async uploadImage(data: {
    scope: MatchMediaScope;
    ownerId: string;
    file: File;
  }): Promise<ArenaMediaAsset> {
    await requireAuthenticatedUser();

    if (!data.ownerId || data.ownerId.includes("/") || data.ownerId.includes("\\")) {
      throw new Error("Destino de midia invalido.");
    }

    if (!MATCH_MEDIA_EXTENSIONS[data.file.type]) {
      throw new Error("Tipo de imagem nao permitido. Use JPG, PNG ou WebP.");
    }

    if (data.file.size > MATCH_MEDIA_MAX_BYTES) {
      throw new Error("Imagem muito pesada. Envie ate 5 MB.");
    }

    const path = `${data.scope}/${data.ownerId}/${buildSafeMediaFileName(data.file)}`;
    const { error } = await supabase.storage.from(MATCH_MEDIA_BUCKET).upload(path, data.file, {
      cacheControl: "31536000",
      contentType: data.file.type,
      upsert: false,
    });

    if (error) throw error;

    const { data: publicUrlData } = supabase.storage.from(MATCH_MEDIA_BUCKET).getPublicUrl(path);

    return {
      url: publicUrlData.publicUrl,
      path,
      bucket: MATCH_MEDIA_BUCKET,
      content_type: data.file.type,
      size: data.file.size,
    };
  },
};
